import { reserveSocForChemistry } from "../battery-reserve";
import { batteryCurrentShares } from "../battery-stack";
import type { DefLookup } from "../catalog";
import type { System } from "../types";
import type { EnergyFlow } from "./energy-flow";

/** Positive means charging. Every series level carries the string current;
 * parallel members share that current by capacity. */
export function batteryCurrentsFromEnergyFlow(system: System, lookup: DefLookup, flow: EnergyFlow): Map<string, number> {
  const nodes = new Map(system.nodes.map(node => [node.id, node]));
  const capacity = (id: string): number => {
    const node = nodes.get(id);
    return node?.kind === "component" ? lookup(node.definitionId, node.customDefinition)?.capacityAh ?? 0 : 0;
  };
  const currents = new Map<string, number>();
  for (const bus of flow.buses.values()) {
    if (bus.nominalV <= 0 || bus.stacks.length === 0) continue;
    const busCurrent = bus.netW / bus.nominalV;
    for (const [id, share] of batteryCurrentShares(bus.stacks, capacity))
      currents.set(id, (currents.get(id) ?? 0) + busCurrent * share);
  }
  return currents;
}

/** Keep inventory totals separate from usable supply. An idle disconnected
 * bank contributes no discharge time, and a charging bank cannot cancel a
 * different bank's discharge. The empty estimate is the first active battery
 * to reach reserve at its present current, including a limiting series cell. */
export function connectedRuntime(
  system: System,
  lookup: DefLookup,
  flow: EnergyFlow,
  batterySoc: Readonly<Record<string, number>>,
  defaultSoc: number,
): { timeToEmptyHours: number; timeToFullHours: number } {
  const currents = batteryCurrentsFromEnergyFlow(system, lookup, flow);
  let timeToEmptyHours = Infinity;
  const chargingTimes: number[] = [];
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (def?.category !== "battery" || !(def.capacityAh != null && def.capacityAh > 0)) continue;
    const current = currents.get(node.id) ?? 0;
    const soc = batterySoc[node.id] ?? defaultSoc;
    if (current < -1e-9) {
      const availableAh = def.capacityAh * Math.max(0, soc - reserveSocForChemistry(def.chemistry));
      timeToEmptyHours = Math.min(timeToEmptyHours, availableAh / -current);
    } else if (current > 1e-9) {
      chargingTimes.push(def.capacityAh * Math.max(0, 1 - soc) / current);
    }
  }
  return { timeToEmptyHours, timeToFullHours: chargingTimes.length > 0 ? Math.max(...chargingTimes) : Infinity };
}
